<template>
  <q-layout>
    <q-page-container>
      <q-page class="q-pa-md">
      <div>
        <q-linear-progress
          :value="loadingValue"
          size="1px"
          reverse
          :color="`${isDarkModeGlobal ? 'grey-10' : 'grey-4'}`"
          class="q-ma-md full-width bg-primary"
        />
        <q-tooltip :offset="[0, 8]">3 seconds before triggering calculation</q-tooltip>
      </div>

        <!-- Top-Level Tabs -->
        <q-tabs
          v-model="activeTab"
          align="justify"
          class="text-grey"
          shrink
          inline-label
          outside-arrows
          mobile-arrows
          active-color="primary"
          indicator-color="primary-10"
        >
          <q-tab name="main" label="Main" />
          <q-tab name="timingGraph" label="Timing Graph" />
          <q-tab name="temperature" label="Temperature" />
          <q-tab name="assumptions" label="Assumptions" />
        </q-tabs>

        <!-- Tab Panels -->
        <q-tab-panels
            v-model="activeTab"
            animated
            :class="`${isDarkModeGlobal ? 'bg-grey-10' : 'bg-grey-4'}`">

          <!-- =============== MAIN TAB =============== -->
          <q-tab-panel name="main">

            <div class="row q-col-gutter-xs">

              <!-- PARAMETERS -->
              <div class="col-12 col-md-6 col-lg-3 q-row-gutter-xs">
                <q-card flat bordered :dark="isDarkModeGlobal" class="q-mb-sm">
                  <q-card-section>
                    <h6 class="text-primary">Parameters</h6>
                    <parameter-inputs />
                  </q-card-section>
                </q-card>

                <q-card flat bordered :dark="isDarkModeGlobal" class="q-mb-sm">
                  <q-card-section>
                    <h6 class="text-primary">Timing</h6>
                    <timing-parameters-inputs />
                  </q-card-section>
                </q-card>

                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <overal-dc-inputs/>
                  </q-card-section>
                </q-card>
              </div>

              <!-- RESULTS -->
              <div class="col-12 col-md-6 col-lg-5">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section class="row justify-between">
                    <h6 class="text-primary">Results</h6>
                    <div class="q-gutter-md">
                      <q-btn
                        round
                        color="primary"
                        @click="transactionStore.exportResults('pdf')"
                        icon="picture_as_pdf"
                      >
                        <q-tooltip>
                          Download PDF report
                        </q-tooltip>

                      </q-btn>
                      <q-btn
                        round
                        color="primary"
                        @click="transactionStore.exportResults('zip')"
                        icon="folder_zip"
                      >
                        <q-tooltip>
                          Download Zipped CSV file containing results
                        </q-tooltip>
                      </q-btn>
                    </div>
                  </q-card-section>
                  <calculator-results/>
                </q-card>
              </div>

              <!-- LIMITS -->
              <div class="col-12 col-md-6 col-lg-4">
                  <svg-plot-view/>
              </div>

            </div>
          </q-tab-panel>

          <!-- =============== TIMING GRAPH TAB =============== -->
          <q-tab-panel name="timingGraph">
            <div class="row q-col-gutter-sm q-row-gutter-sm">
              <div class="col-12 col-md-6 col-lg-4">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <h6 class="text-primary">Timing Graph</h6>
                    <timing-parameters-inputs />
                  </q-card-section>
                </q-card>
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <overal-dc-inputs/>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col-12 col-md-6 col-lg-8">
                    <timing-docs/>
              </div>
            </div>
          </q-tab-panel>

          <!-- =============== TEMPERATURE TAB =============== -->
          <q-tab-panel name="temperature">
            <div class="row q-col-gutter-sm q-row-gutter-sm">

              <!-- Heat Deposition in Tissue -->
              <div class="col-12 col-md-6">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <h6 class="text-primary">Heat Deposition in Tissue [Work in Progress]
                      <q-tooltip>Work in progress</q-tooltip></h6>
                    <heat-deposition-card/>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Acoustic Power -->
              <div class="col-12 col-md-6">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <h6 class="text-primary">Acoustic Power</h6>
                    <acoustic-power-card/>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Estimates of Thermal Metrics -->
              <div class="col-12">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <card-temperature-change />
                </q-card>
              </div>

            </div>
          </q-tab-panel>

          <!-- =============== ASSUMPTIONS TAB =============== -->
          <q-tab-panel name="assumptions">
            <div class="row q-col-gutter-sm q-row-gutter-sm">

              <!-- Assumptions -->
              <div class="col-12 col-md-6 col-lg-3">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <assumptions-card/>
                </q-card>
              </div>

              <!-- Derating Values -->
              <div class="col-12 col-md-6 col-lg-3">
                <q-card flat bordered :dark="isDarkModeGlobal">
                    <derating-values-card/>
                </q-card>
              </div>

              <!-- Skull -->
              <div class="col-12 col-md-6 col-lg-3">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <h6 class="text-primary">Skull</h6>
                    <skull-values-card/>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Brain -->
              <div class="col-12 col-md-6 col-lg-3">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-card-section>
                    <h6 class="text-primary">Brain</h6>
                    <brain-values-card/>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Lookup Table -->
              <div class="col-12">
                <q-card flat bordered :dark="isDarkModeGlobal">
                  <q-toolbar>
                    <q-toolbar-title class="text-primary">Lookup Table</q-toolbar-title>
                    <q-btn
                      icon="book"
                      color="primary"
                      dense
                      flat
                      @click="openInNewTab('https://doi.org/10.1016/j.brs.2022.12.005')"
                    >
                      <q-tooltip>Show reference in new tab</q-tooltip>
                    </q-btn>

                    <q-btn icon="help" color="primary" dense flat>
                      <q-tooltip>Show note</q-tooltip>
                      <q-popup-proxy :offset="[-70, 20]">
                        <q-banner :dark="isDarkModeGlobal">
                          <template #avatar>
                            <q-icon name="help" color="primary" />
                          </template>
                          When the ultrasound frequency (kHz) or the ultrasound beam diameter
                          on the skull surface (mm) exceeds the table limits, the pressure
                          transmission value from the last known limit will be applied.
                        </q-banner>
                      </q-popup-proxy>
                    </q-btn>
                  </q-toolbar>
                  <q-card-section class="text-center flex justify-center">
                    <heat-map-view v-if="showChart2" />
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </q-tab-panel>
        </q-tab-panels>
      </q-page>
    </q-page-container>
  </q-layout>
</template>


<script setup lang="ts">
import CalculatorResults from "./CalculatorResults.vue"
import CardTemperatureChange from "./CardTemperatureChange.vue"
import ParameterInputs from "./TusCalculatorParameterInputs.vue"
import { useCalculatorStore } from '../stores/calculator.store'
import {nextTick, ref, watch} from 'vue'
import {storeToRefs} from "pinia"
import HeatMapView from "./HeatMapView.vue";
import TimingParametersInputs from "./TimingParametersInputs.vue";
import HeatDepositionCard from "./HeatDepositionCard.vue";
import AcousticPowerCard from "./AcousticPowerCard.vue";
import AssumptionsCard from "./AssumptionsCard.vue";
import DeratingValuesCard from "./DeratingValuesCard.vue";
import SkullValuesCard from "./SkullValuesCard.vue";
import BrainValuesCard from "./BrainValuesCard.vue";
import TimingDocs from "./TimingDocs.vue";
import SvgPlotView from "./SvgPlotView.vue";
const calculatorStore = useCalculatorStore()
const {
  isDarkModeGlobal,
  loadingValue
} = storeToRefs(calculatorStore)

import {useTransactionStore} from '../stores/transaction.store'
import OveralDcInputs from "./OveralDcInputs.vue";
const transactionStore = useTransactionStore()
const activeTab = ref('main')
const showChart = ref(true)
const showChart2 = ref(true)

// Recreate the chart when toggling dark mode
const toggleDarkMode = () => {
  showChart.value = false
  showChart2.value = false
  nextTick(() => {
    showChart.value = true
    showChart2.value = true
  });
};

watch(isDarkModeGlobal, () => {
  toggleDarkMode()
})

function openInNewTab(url) {
  window.open(url, '_blank', 'noreferrer')
}
</script>